import { useCallback, useState, type ReactNode } from 'react';
import { useModalDialog } from './useModalDialog';

export interface PromptRequest {
  /** Names the dialog to a screen reader as well as heading it, the way every other modal here does. */
  title: string;
  label: string;
  defaultValue?: string;
  confirmLabel?: string;
  /** Every caller so far refuses a blank answer, so the confirm stays shut until something is typed. */
  allowEmpty?: boolean;
  /** The server's own limit, so a long answer is cut while typing rather than refused after confirming. */
  maxLength?: number;
}

export interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel?: string;
  /** Names the way out when the action is itself a cancel, so the two buttons do not both say "Cancel". */
  cancelLabel?: string;
}

/** A confirm that cannot be agreed to until one of a fixed set of answers is picked. */
export interface ChoiceRequest {
  title: string;
  message: string;
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  /** Preselects an answer such as the stored one; ignored unless it is an option, as the select cannot show it. */
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

/** A confirmation whose two affirmative choices produce different writes. */
interface BinaryChoiceRequest extends ConfirmRequest {
  secondaryLabel: string;
}

type Pending =
  | { kind: 'prompt'; request: PromptRequest; settle: (answer: string | null) => void }
  | { kind: 'confirm'; request: ConfirmRequest; settle: (agreed: boolean) => void }
  | { kind: 'choice'; request: ChoiceRequest; settle: (choice: string | null) => void }
  | { kind: 'binaryChoice'; request: BinaryChoiceRequest; settle: (answer: 'primary' | 'secondary' | null) => void };

interface PromptModalProps {
  request: PromptRequest;
  onSubmit: (answer: string) => void;
  onCancel: () => void;
}

const PromptModal = ({ request, onSubmit, onCancel }: PromptModalProps) => {
  const [value, setValue] = useState(request.defaultValue ?? '');
  const dialogRef = useModalDialog(onCancel);

  const answer = value.trim();
  const blocked = !request.allowEmpty && answer === '';

  return (
    <dialog className="wms-prompt" ref={dialogRef} aria-label={request.title}>
      <header>
        {request.title}
        <button type="button" className="x" aria-label="Close" onClick={onCancel}>
          ✕
        </button>
      </header>
      <div className="body">
        <div className="qrow">
          <b>{request.label}</b>
          <input
            type="text"
            aria-label={request.label}
            maxLength={request.maxLength}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            // Enter submits, because the prompt this replaced did and the desk types one-handed.
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !blocked) {
                onSubmit(answer);
              }
            }}
          />
        </div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-blue btn-sm" disabled={blocked} onClick={() => onSubmit(answer)}>
          {request.confirmLabel ?? 'OK'}
        </button>
      </div>
    </dialog>
  );
};

interface ConfirmModalProps {
  request: ConfirmRequest;
  onAgree: () => void;
  onCancel: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}

const ConfirmModal = ({ request, onAgree, onCancel, secondaryLabel, onSecondary }: ConfirmModalProps) => {
  const dialogRef = useModalDialog(onCancel);

  return (
    <dialog className="wms-prompt" ref={dialogRef} aria-label={request.title}>
      <header>
        {request.title}
        <button type="button" className="x" aria-label="Close" onClick={onCancel}>
          ✕
        </button>
      </header>
      <div className="body">{request.message}</div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onCancel}>
          {request.cancelLabel ?? 'Cancel'}
        </button>
        {secondaryLabel && (
          <button type="button" className="btn btn-line btn-sm" onClick={onSecondary}>
            {secondaryLabel}
          </button>
        )}
        <button type="button" className="btn btn-red-line btn-sm" onClick={onAgree}>
          {request.confirmLabel ?? 'OK'}
        </button>
      </div>
    </dialog>
  );
};

interface ChoiceModalProps {
  request: ChoiceRequest;
  onChoose: (choice: string) => void;
  onCancel: () => void;
}

const ChoiceModal = ({ request, onChoose, onCancel }: ChoiceModalProps) => {
  const [value, setValue] = useState(() =>
    request.options.some((option) => option.value === request.defaultValue) ? (request.defaultValue ?? '') : '',
  );
  const dialogRef = useModalDialog(onCancel);

  return (
    <dialog className="wms-prompt" ref={dialogRef} aria-label={request.title}>
      <header>
        {request.title}
        <button type="button" className="x" aria-label="Close" onClick={onCancel}>
          ✕
        </button>
      </header>
      <div className="body">
        <p className="msg">{request.message}</p>
        <div className="qrow">
          <b>{request.label}</b>
          <select aria-label={request.label} value={value} onChange={(event) => setValue(event.target.value)}>
            <option value="" disabled>
              Choose…
            </option>
            {request.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onCancel}>
          {request.cancelLabel ?? 'Cancel'}
        </button>
        <button type="button" className="btn btn-blue btn-sm" disabled={value === ''} onClick={() => onChoose(value)}>
          {request.confirmLabel ?? 'OK'}
        </button>
      </div>
    </dialog>
  );
};

/**
 * The in-page replacement for `window.prompt` and `window.confirm`. It stays promise-shaped so a call
 * site reads the way the imperative one did — ask, then act on the answer — instead of splintering into
 * an open handler and a confirm handler for every one of a screen's dozen questions.
 *
 * The answer comes back trimmed, and `null`/`false` mean the operator backed out. A caller then simply
 * stops — no write and no notification: the dialog closing is the answer, and a red "Cancelled" toast
 * read as a failure of something the operator had chosen not to do.
 */
export const usePromptDialog = () => {
  const [pending, setPending] = useState<Pending | null>(null);

  const ask = useCallback(
    (request: PromptRequest) => new Promise<string | null>((settle) => setPending({ kind: 'prompt', request, settle })),
    [],
  );

  const askConfirm = useCallback(
    (request: ConfirmRequest) => new Promise<boolean>((settle) => setPending({ kind: 'confirm', request, settle })),
    [],
  );

  const askChoice = useCallback(
    (request: ChoiceRequest) => new Promise<string | null>((settle) => setPending({ kind: 'choice', request, settle })),
    [],
  );

  const askBinaryChoice = useCallback(
    (request: BinaryChoiceRequest) =>
      new Promise<'primary' | 'secondary' | null>((settle) => setPending({ kind: 'binaryChoice', request, settle })),
    [],
  );

  let dialog: ReactNode = null;
  if (pending?.kind === 'prompt') {
    const { request, settle } = pending;
    dialog = (
      <PromptModal
        request={request}
        onSubmit={(answer) => {
          setPending(null);
          settle(answer);
        }}
        onCancel={() => {
          setPending(null);
          settle(null);
        }}
      />
    );
  } else if (pending?.kind === 'binaryChoice') {
    const { request, settle } = pending;
    const finish = (answer: 'primary' | 'secondary' | null) => {
      setPending(null);
      settle(answer);
    };
    dialog = (
      <ConfirmModal
        request={request}
        onAgree={() => finish('primary')}
        secondaryLabel={request.secondaryLabel}
        onSecondary={() => finish('secondary')}
        onCancel={() => finish(null)}
      />
    );
  } else if (pending?.kind === 'confirm') {
    const { request, settle } = pending;
    dialog = (
      <ConfirmModal
        request={request}
        onAgree={() => {
          setPending(null);
          settle(true);
        }}
        onCancel={() => {
          setPending(null);
          settle(false);
        }}
      />
    );
  } else if (pending?.kind === 'choice') {
    const { request, settle } = pending;
    dialog = (
      <ChoiceModal
        request={request}
        onChoose={(choice) => {
          setPending(null);
          settle(choice);
        }}
        onCancel={() => {
          setPending(null);
          settle(null);
        }}
      />
    );
  }

  return { ask, askConfirm, askChoice, askBinaryChoice, dialog };
};
